<script setup lang="ts">
import type { Schemas } from '#shopware'
import { getCategoryRoute } from '@shopware/helpers'

const route = useRoute()
const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)

// Get navigation items from mega menu
const { navigationItems } = useMegaMenu()

// Recursive function to find a category by ID in the navigation tree
function findCategoryById(
    id: string,
    items: Schemas['Category'][] | null | undefined
): Schemas['Category'] | undefined {
    if (!items) return undefined

    for (const item of items) {
        if (item.id === id) return item

        if (item.children?.length) {
            const found = findCategoryById(id, item.children)
            if (found) return found
        }
    }

    return undefined
}

// Find current category by matching route.path with seoUrls[0].seoPathInfo (exact match only)
function findCategoryBySeoUrl(
    path: string,
    items: Schemas['Category'][] | null | undefined
): Schemas['Category'] | undefined {
    if (!items) return undefined

    // Remove locale prefix and trailing slash from path (e.g., /de/ or /en/)
    const cleanPath = path.replace(/^\/de\/|^\/en\/|^\//, '').replace(/\/$/, '')

    for (const item of items) {
        // Check if this category's seoUrl matches the current route path (exact match)
        const seoPath = item.seoUrls?.[0]?.seoPathInfo?.replace(/\/$/, '')

        if (seoPath && cleanPath === seoPath) {
            return item
        }

        // Recursively search children
        if (item.children?.length) {
            const found = findCategoryBySeoUrl(path, item.children)
            if (found) return found
        }
    }

    return undefined
}

// Get current category from navigation items based on route
const currentCategory = computed(() => {
    return findCategoryBySeoUrl(route.path, navigationItems.value)
})

// Get quick link categories
const quickLinkCategories = computed(() => {
    // Get quickLinks IDs from current category's customFields
    const customFields = currentCategory.value?.customFields as Record<string, unknown> | undefined
    const quickLinksIds = customFields?.quickLinks as string[] | undefined

    if (!quickLinksIds || !Array.isArray(quickLinksIds) || quickLinksIds.length === 0) {
        return []
    }

    // Find each category by ID
    const categories = quickLinksIds
        .map(id => findCategoryById(id, navigationItems.value))
        .filter((cat): cat is Schemas['Category'] => cat !== undefined)

    return categories
})
</script>

<template>
    <nav
        v-if="quickLinkCategories?.length > 0"
        itemscope
        itemtype="https://schema.org/SiteNavigationElement"
        :aria-label="$t('megaMenu.subNavigation.ariaLabel', { category: currentCategory?.name ?? '' })"
        class="w-full bg-base-100 border-b border-border"
    >
        <div class="m-auto w-full max-w-8xl px-3">
            <div class="flex items-center gap-2">
                <ul class="flex items-center flex-nowrap overflow-x-scroll lg:overflow-x-hidden whitespace-nowrap gap-3 py-3">
                    <li
                        v-for="item in quickLinkCategories"
                        :key="item.id"
                        class="flex items-center"
                    >
                        <FoundationLink
                            :href="formatLink(getCategoryRoute(item))"
                            itemprop="url"
                            class="text-sm text-primary hover:text-primary-shade-dark transition-colors"
                        >
                            <span itemprop="name">{{ item.name }}</span>
                        </FoundationLink>
                    </li>
                </ul>
            </div>
        </div>
    </nav>
</template>